'use client';

import { createClient } from '@/lib/supabase/client';

/**
 * Client Stories (testimonials).
 *
 * Source of truth is the `client_stories` table in the project's existing
 * Supabase database — see supabase/migrations for the schema and its seed.
 *
 * The defaults below are the six testimonials the site shipped with, kept in
 * code for two reasons: they are the seed the migration and the admin panel's
 * "restore defaults" action both insert, and they are what the public section
 * falls back to when Supabase is unconfigured or unreachable. That fallback is
 * what stops a database outage — or a deploy without env vars — from emptying
 * a marketing section.
 */

export interface ClientStory {
  id: string;
  /** Stable natural key. Seeding upserts on this, so re-running cannot
   *  duplicate a story. */
  slug: string;
  name: string;
  role: string;
  company: string;
  industry: string;
  /** Initials shown in the square avatar tile. */
  avatar: string;
  quote: string;
  result: string;
  rating: number;
  displayOrder: number;
  isPublished: boolean;
}

/** Shape of a row as stored in Postgres (snake_case). */
interface ClientStoryRow {
  id: string;
  slug: string;
  name: string;
  role: string;
  company: string;
  industry: string;
  avatar: string;
  quote: string;
  result: string;
  rating: number | null;
  display_order: number | null;
  is_published: boolean | null;
}

export const CLIENT_STORIES_TABLE = 'client_stories';

/** Derive the avatar initials from a name, used when the admin leaves it blank. */
export function initialsFrom(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Slugify a name/company pair into the stable natural key. */
export function slugFrom(name: string, company: string): string {
  return `${name} ${company}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function rowToStory(row: ClientStoryRow): ClientStory {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    role: row.role ?? '',
    company: row.company ?? '',
    industry: row.industry ?? '',
    avatar: row.avatar || initialsFrom(row.name ?? ''),
    quote: row.quote ?? '',
    result: row.result ?? '',
    rating: row.rating ?? 5,
    displayOrder: row.display_order ?? 0,
    isPublished: row.is_published ?? true,
  };
}

/** Payload for insert/update. `id` is omitted so Postgres owns it. */
export function storyToRow(story: Omit<ClientStory, 'id'>) {
  return {
    slug: story.slug,
    name: story.name,
    role: story.role,
    company: story.company,
    industry: story.industry,
    avatar: story.avatar || initialsFrom(story.name),
    quote: story.quote,
    result: story.result,
    rating: story.rating,
    display_order: story.displayOrder,
    is_published: story.isPublished,
  };
}

/**
 * The six stories the site launched with, verbatim. Order and wording match
 * the original hardcoded list so the public section is unchanged after
 * migration.
 */
export const DEFAULT_CLIENT_STORIES: ClientStory[] = [
  {
    id: 'seed-1',
    slug: 'sarah-mitchell-bloom-hospitality-group',
    name: 'Sarah Mitchell',
    role: 'Founder & CEO',
    company: 'Bloom Hospitality Group',
    industry: 'Hospitality',
    avatar: 'SM',
    quote:
      'Noventi Labs completely transformed how we approach digital marketing. Within three months, our online bookings increased significantly and our brand finally felt cohesive across every channel. Their team understood our industry from day one.',
    result: 'Online bookings up 40% in 90 days',
    rating: 5,
    displayOrder: 1,
    isPublished: true,
  },
  {
    id: 'seed-2',
    slug: 'james-okafor-kova-commerce',
    name: 'James Okafor',
    role: 'Head of Growth',
    company: 'Kova Commerce',
    industry: 'E-commerce',
    avatar: 'JO',
    quote:
      'We had tried multiple agencies before Noventi Labs. What sets them apart is that they actually dig into the data and build strategies around what the numbers say — not what sounds good in a pitch deck. Our ROAS improved dramatically.',
    result: 'ROAS improved from 1.8x to 4.1x',
    rating: 5,
    displayOrder: 2,
    isPublished: true,
  },
  {
    id: 'seed-3',
    slug: 'priya-nair-meridian-financial-services',
    name: 'Priya Nair',
    role: 'Marketing Director',
    company: 'Meridian Financial Services',
    industry: 'Financial Services',
    avatar: 'PN',
    quote:
      'The business assessment alone was worth the engagement. It gave us a clear picture of where our marketing was leaking revenue and exactly what to fix first. The consulting team was sharp, responsive, and genuinely invested in our outcomes.',
    result: 'Lead quality score improved by 65%',
    rating: 5,
    displayOrder: 3,
    isPublished: true,
  },
  {
    id: 'seed-4',
    slug: 'tom-bergstrom-nordvik-tech',
    name: 'Tom Bergström',
    role: 'Co-founder',
    company: 'Nordvik Tech',
    industry: 'SaaS / Technology',
    avatar: 'TB',
    quote:
      'We were scaling fast but our marketing was completely ad hoc. Noventi Labs helped us build a proper growth framework — from positioning to paid acquisition to retention. It felt like having a senior CMO on call without the full-time cost.',
    result: 'MRR grew 3x over 6 months',
    rating: 5,
    displayOrder: 4,
    isPublished: true,
  },
  {
    id: 'seed-5',
    slug: 'amara-diallo-soleil-wellness-clinics',
    name: 'Amara Diallo',
    role: 'Operations Manager',
    company: 'Soleil Wellness Clinics',
    industry: 'Health & Wellness',
    avatar: 'AD',
    quote:
      'Our biggest challenge was standing out in a crowded local market. Noventi Labs developed a brand positioning strategy and local SEO plan that put us on the map — literally. We now rank top 3 for all our key services in two cities.',
    result: 'Top 3 local rankings in 2 cities',
    rating: 5,
    displayOrder: 5,
    isPublished: true,
  },
  {
    id: 'seed-6',
    slug: 'lucas-ferreira-vantage-retail-group',
    name: 'Lucas Ferreira',
    role: 'CEO',
    company: 'Vantage Retail Group',
    industry: 'Retail',
    avatar: 'LF',
    quote:
      "The paid advertising strategy Noventi Labs built for us was a game changer. They didn't just set up campaigns — they built a full-funnel system with proper tracking, creative testing, and monthly optimisation. Every dollar we spend now works harder.",
    result: 'Cost per acquisition reduced by 52%',
    rating: 5,
    displayOrder: 6,
    isPublished: true,
  },
];

const byOrder = (a: ClientStory, b: ClientStory) =>
  a.displayOrder - b.displayOrder || a.name.localeCompare(b.name);

export type LoadState = 'loading' | 'ready' | 'fallback';

export interface LoadResult {
  stories: ClientStory[];
  state: LoadState;
  /** Set when Supabase answered with an error, for the admin panel to show. */
  error?: string;
}

/**
 * Published stories for the public section. Never rejects and never returns an
 * empty list while defaults exist — an unconfigured or failing backend falls
 * back to the seed rather than blanking the section.
 */
export async function loadPublishedClientStories(): Promise<LoadResult> {
  const supabase = createClient();
  if (!supabase) return { stories: [], state: 'fallback' };

  try {
    const { data, error } = await supabase
      .from(CLIENT_STORIES_TABLE)
      .select('*')
      .eq('is_published', true)
      .order('display_order', { ascending: true });

    if (error) return { stories: [], state: 'fallback', error: error.message };

    const rows = (data ?? []) as ClientStoryRow[];
    // Nothing published means nothing published. This used to fall back to
    // DEFAULT_CLIENT_STORIES, which put six invented names and their invented
    // results — "bookings up 40% in 90 days" — on the public homepage under a
    // heading promising real feedback. A testimonial nobody gave is not a
    // placeholder, it is a claim, so the public page now shows none until real
    // ones exist. The defaults stay in the file as the example copy the admin
    // panel writes its form placeholders from.
    if (rows.length === 0) return { stories: [], state: 'fallback' };

    return { stories: rows.map(rowToStory).sort(byOrder), state: 'ready' };
  } catch (err) {
    return {
      stories: [],
      state: 'fallback',
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Every story, published or not — the admin list.
 *
 * When the table is unreachable or empty the defaults are returned in
 * `fallback` state, so the admin sees the same six stories the public site is
 * rendering rather than an empty screen that contradicts it. They are flagged
 * via `isSeed` so the panel can show them read-only until they are imported —
 * editing a row that does not exist in the database yet would silently fail.
 */
export async function loadAllClientStories(): Promise<LoadResult> {
  const supabase = createClient();
  if (!supabase) {
    return {
      stories: DEFAULT_CLIENT_STORIES,
      state: 'fallback',
      error:
        'Supabase is not configured. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY to manage Client Stories.',
    };
  }

  try {
    const { data, error } = await supabase
      .from(CLIENT_STORIES_TABLE)
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      return { stories: DEFAULT_CLIENT_STORIES, state: 'fallback', error: error.message };
    }

    const rows = (data ?? []) as ClientStoryRow[];
    // Table exists but the seed has not run — mirror the public fallback.
    if (rows.length === 0) return { stories: DEFAULT_CLIENT_STORIES, state: 'fallback' };

    return {
      stories: rows.map(rowToStory).sort(byOrder),
      state: 'ready',
    };
  } catch (err) {
    return {
      stories: DEFAULT_CLIENT_STORIES,
      state: 'fallback',
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

export async function createClientStory(story: Omit<ClientStory, 'id'>): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase.from(CLIENT_STORIES_TABLE).insert(storyToRow(story));
  return error ? error.message : null;
}

export async function updateClientStory(
  id: string,
  story: Omit<ClientStory, 'id'>
): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase
    .from(CLIENT_STORIES_TABLE)
    .update({ ...storyToRow(story), updated_at: new Date().toISOString() })
    .eq('id', id);
  return error ? error.message : null;
}

export async function deleteClientStory(id: string): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase.from(CLIENT_STORIES_TABLE).delete().eq('id', id);
  return error ? error.message : null;
}

/**
 * Insert the six launch stories. Idempotent: `slug` is unique and conflicts
 * are ignored, so running this after the SQL migration — or twice by hand —
 * adds nothing and edits nothing.
 */
export async function seedDefaultClientStories(): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const rows = DEFAULT_CLIENT_STORIES.map(({ id: _id, ...rest }) => storyToRow(rest));
  const { error } = await supabase
    .from(CLIENT_STORIES_TABLE)
    .upsert(rows, { onConflict: 'slug', ignoreDuplicates: true });
  return error ? error.message : null;
}
